import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ArrowLeft, MapPin, Clock, MoreVertical, XCircle, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_MAP = {
  scheduled: { label: 'Programmato', cls: 'status-scheduled' },
  in_progress: { label: 'In corso', cls: 'status-in_progress' },
  completed: { label: 'Completato', cls: 'status-completed' },
  cancelled: { label: 'Annullato', cls: 'status-cancelled' },
};

const PAYMENT_MAP = {
  pending: { label: 'In attesa', cls: 'status-pending' },
  online: { label: 'Online', cls: 'status-paid' },
  on_site: { label: 'In loco', cls: 'status-scheduled' },
};

export default function MyAppointments() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/appointments')
      .then((res) => setAppointments(res.data))
      .catch(() => toast.error('Errore caricamento'))
      .finally(() => setLoading(false));
  }, []);

  const cancelAppointment = async (aptId) => {
    try {
      await api.put(`/appointments/${aptId}/status`, { status: 'cancelled' });
      setAppointments((prev) =>
        prev.map((a) => a.appointment_id === aptId ? { ...a, status: 'cancelled' } : a)
      );
      toast.success('Appuntamento annullato');
    } catch {
      toast.error('Errore');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-stone-50">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const upcoming = appointments.filter((a) => a.status === 'scheduled' || a.status === 'in_progress');
  const past = appointments.filter((a) => a.status === 'completed' || a.status === 'cancelled');

  const AppointmentCard = ({ apt }) => (
    <Card className="border-0 shadow-sm rounded-xl" data-testid={`apt-card-${apt.appointment_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex flex-col items-center justify-center">
              <span className="text-[10px] text-blue-600 font-bold leading-tight">
                {new Date(apt.date).toLocaleDateString('it-IT', { month: 'short' })}
              </span>
              <span className="text-lg font-bold text-blue-700 leading-tight">{new Date(apt.date).getDate()}</span>
            </div>
            <div>
              <p className="font-semibold text-sm text-slate-900 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-slate-400" /> {apt.time_slot}
              </p>
              <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3" /> Zona {apt.zone}
                {apt.address && <> - {apt.address}</>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${STATUS_MAP[apt.status]?.cls || 'status-pending'} border-0 text-xs px-2 py-0.5`}>
              {STATUS_MAP[apt.status]?.label || apt.status}
            </Badge>
            {apt.status === 'scheduled' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`apt-menu-${apt.appointment_id}`}>
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => cancelAppointment(apt.appointment_id)} className="text-red-600" data-testid={`cancel-apt-${apt.appointment_id}`}>
                    <XCircle className="w-4 h-4 mr-2" /> Annulla
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        <Separator className="my-2" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge className={`${PAYMENT_MAP[apt.payment_method]?.cls || 'status-pending'} border-0 text-xs px-2 py-0.5`}>
              Pagamento: {PAYMENT_MAP[apt.payment_method]?.label || apt.payment_method}
            </Badge>
          </div>
          <p className="text-sm font-bold text-slate-900">
            EUR {apt.price_min} - {apt.price_max}
          </p>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-stone-50" data-testid="appointments-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="apt-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <p className="text-sm font-bold text-slate-900">I Miei Appuntamenti</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <Tabs defaultValue="upcoming" className="w-full">
          <TabsList className="mb-6 bg-white rounded-xl shadow-sm p-1">
            <TabsTrigger value="upcoming" className="rounded-lg text-xs" data-testid="tab-upcoming">
              Prossimi ({upcoming.length})
            </TabsTrigger>
            <TabsTrigger value="past" className="rounded-lg text-xs" data-testid="tab-past">
              Passati ({past.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            {upcoming.length === 0 ? (
              <Card className="border-0 shadow-sm rounded-xl">
                <CardContent className="p-8 text-center">
                  <CalendarDays className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">Nessun appuntamento programmato</p>
                  <Button
                    variant="outline"
                    className="rounded-full mt-4 text-xs"
                    onClick={() => navigate('/preventivo')}
                    data-testid="new-quote-from-apt"
                  >
                    Crea un preventivo
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {upcoming.map((apt) => <AppointmentCard key={apt.appointment_id} apt={apt} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past">
            {past.length === 0 ? (
              <Card className="border-0 shadow-sm rounded-xl">
                <CardContent className="p-8 text-center">
                  <p className="text-sm text-slate-500">Nessun appuntamento passato</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {past.map((apt) => <AppointmentCard key={apt.appointment_id} apt={apt} />)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
